import {
  getMessaging,
  getToken,
  isSupported,
  Messaging,
} from "firebase/messaging";
import { app } from "../firebase";
import * as Sentry from "@sentry/react";

let messagingInstancePromise: Promise<Messaging | null> | null = null;

const resolveMessagingInstance = async (): Promise<Messaging | null> => {
  if (!messagingInstancePromise) {
    messagingInstancePromise = isSupported()
      .then((supported) => {
        return supported ? getMessaging(app) : null;
      })
      .catch((error) => {
        Sentry.captureException(error);
        return null;
      });
  }

  return messagingInstancePromise;
};

const ensureServiceWorkerRegistration = async () => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }

  try {
    const existingRegistration = await navigator.serviceWorker.getRegistration(
      "/firebase-messaging-sw.js"
    );

    if (existingRegistration) {
      // Wait for the service worker to become active
      if (existingRegistration.active) {
        return existingRegistration;
      }

      // If installing or waiting, wait for it to become active
      if (existingRegistration.installing || existingRegistration.waiting) {
        await new Promise<void>((resolve) => {
          const sw = existingRegistration.installing || existingRegistration.waiting;
          if (sw) {
            sw.addEventListener("statechange", function handler() {
              if (sw.state === "activated") {
                sw.removeEventListener("statechange", handler);
                resolve();
              }
            });
          } else {
            resolve();
          }
        });
      }

      return existingRegistration;
    }

    const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
      scope: "/",
    });

    // Wait for the service worker to become active
    if (registration.installing) {
      await new Promise<void>((resolve) => {
        registration.installing!.addEventListener("statechange", function handler(e) {
          const sw = e.target as ServiceWorker;
          if (sw.state === "activated") {
            sw.removeEventListener("statechange", handler);
            resolve();
          }
        });
      });
    } else if (registration.waiting) {
      // If there's a waiting worker, it needs to be activated
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
      await new Promise<void>((resolve) => {
        registration.waiting!.addEventListener("statechange", function handler(e) {
          const sw = e.target as ServiceWorker;
          if (sw.state === "activated") {
            sw.removeEventListener("statechange", handler);
            resolve();
          }
        });
      });
    }

    return registration;
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      tags: { feature: "service_worker_registration" },
    });
    return null;
  }
};

const ensureNotificationPermission = async () => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "denied";
  }

  if (Notification.permission === "granted") {
    return "granted";
  }

  if (Notification.permission === "denied") {
    return "denied";
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    Sentry.captureException(error);
    return "denied";
  }
};

export const getFirebaseMessagingToken = async () => {
  const permission = await ensureNotificationPermission();

  if (permission !== "granted") {
    return null;
  }

  const messagingInstance = await resolveMessagingInstance();
  if (!messagingInstance) {
    return null;
  }

  const registration = await ensureServiceWorkerRegistration();
  if (!registration) {
    return null;
  }

  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!vapidKey) {
    return null;
  }

  try {
    const token = await getToken(messagingInstance, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });

    if (!token) {
      return null;
    }

    return token;
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      tags: { feature: "fcm_token" },
    });
    return null;
  }
};
